/**
 * "Which dataset?" — the replace flow's mandatory first step (client, 2026-09-18).
 *
 * Update/Replace Existing Data is the upload wizard with this in front of it. The target is
 * part of `POST /uploads` — it is what the server versions against — so it has to be settled
 * before a byte moves, and a reader who chose the Replace button has already decided that
 * there *is* a target. Next stays dead until one is named.
 *
 * The list is `versionTargets`, the same narrowing the dropzone's select uses: the manager's
 * own published datasets only, because a harvested or seeded one would be overwritten by the
 * next pipeline run.
 */
import { useTranslation } from 'react-i18next';

import type { ManagedDatasetRow } from '../../lib/api';

export interface ReplaceTargetStepProps {
  datasets: ManagedDatasetRow[];
  value: string | null;
  onPick: (datasetId: string | null) => void;
}

export function ReplaceTargetStep({ datasets, value, onPick }: ReplaceTargetStepProps) {
  const { t } = useTranslation();
  const chosen = datasets.find((d) => d.id === value) ?? null;

  return (
    <div className="mgr-step" data-testid="mgr-step-target">
      <label className="mgr-field" htmlFor="mgr-replace-target">
        <span className="mgr-label">
          {t('manager.replace.which')} <b aria-hidden="true">*</b>
        </span>
        <select
          id="mgr-replace-target"
          data-testid="mgr-replace-target"
          className="mgr-input"
          value={value ?? ''}
          onChange={(e) => onPick(e.target.value || null)}
        >
          <option value="">{t('manager.replace.choose')}</option>
          {datasets.map((d) => (
            <option key={d.id} value={d.id}>
              {`${d.title} — ${d.id}`}
            </option>
          ))}
        </select>
        <span className="mgr-hint">
          {datasets.length === 0 ? t('manager.replace.empty') : t('manager.replace.hint')}
        </span>
      </label>

      {chosen ? (
        <p className="mgr-versionOf" data-testid="mgr-replace-chosen">
          {t('manager.versionOf.target', { title: chosen.title })} <code>{chosen.id}</code>
        </p>
      ) : null}
    </div>
  );
}

export default ReplaceTargetStep;
